/**
 * A placement's tint on a Three material, live (photoRender.ts
 * TexturePlacement `tint`, `tintStrength`): the picture keeps its light and
 * dark and takes the tint's hue and saturation, mixed in by the strength —
 * what Blender's Mix node does in its Color blend (render_room.py pbr_nodes),
 * in the same linear colour space, so the view and the render agree.
 *
 * The material's shader is patched once (onBeforeCompile, after the colour
 * map is read); the tint's uniforms are the material's own and change
 * without a recompile.
 */

import * as THREE from 'three';

interface TintUniforms { uTint: { value: THREE.Color }; uTintStrength: { value: number } }

const GLSL_HSV = `
vec3 tintRgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}
vec3 tintHsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}
uniform vec3 uTint;
uniform float uTintStrength;
`;

const GLSL_APPLY = `
#include <map_fragment>
if (uTintStrength > 0.0) {
  vec3 tintHsv = tintRgb2hsv(uTint);
  vec3 baseHsv = tintRgb2hsv(diffuseColor.rgb);
  vec3 tinted = tintHsv2rgb(vec3(tintHsv.x, tintHsv.y, baseHsv.z));
  diffuseColor.rgb = mix(diffuseColor.rgb, tinted, uTintStrength);
}
`;

/**
 * Put `tint` on the material at `strength` (0: the picture as shot). The
 * first call patches the material's shader; later calls only move the
 * numbers.
 */
export function applyTint(material: THREE.MeshStandardMaterial, tint: string, strength: number): void {
  let u = material.userData.tint as TintUniforms | undefined;
  if (!u) {
    u = { uTint: { value: new THREE.Color(tint) }, uTintStrength: { value: strength } };
    material.userData.tint = u;
    const tintUniforms = u;
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uTint = tintUniforms.uTint;
      shader.uniforms.uTintStrength = tintUniforms.uTintStrength;
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>\n${GLSL_HSV}`)
        .replace('#include <map_fragment>', GLSL_APPLY);
    };
    material.customProgramCacheKey = () => 'tint';
    material.needsUpdate = true;
    return;
  }
  u.uTint.value.set(tint);
  u.uTintStrength.value = strength;
}

/** The tint a placement carries, when it carries one worth drawing (a strength above 0). */
export function tintOf(p: { tint?: string; tintStrength?: number } | null | undefined): { tint: string; strength: number } {
  return { tint: p?.tint ?? '#ffffff', strength: p?.tintStrength ?? 0 };
}
